import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftRight, ArrowRight, ArrowUpDown, Loader2, Repeat, Route, Split, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { money, when } from "@/lib/rides";
import { LocationPicker } from "@/components/LocationPicker";
import { QuoteCard, QuoteProblem } from "@/components/QuoteCard";
import { StopsEditor } from "@/components/booking/StopsEditor";
import { TripOptions, TypeOptions, earliestLocal, toLocalInput } from "@/components/booking/TripOptions";
import { CheckoutPanel } from "@/components/booking/CheckoutPanel";
import { ScheduleNotice } from "@/components/booking/ScheduleNotice";
import { SplitLegs, SplitNotice } from "@/components/rider/SplitLegs";
import { Review } from "@/components/rider/Review";
import { Segmented, SectionTitle } from "@/components/rider/Nav";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const NOW_KINDS = [["one_way", "One Way", "One pickup and one destination", ArrowRight], ["round_trip", "Round Trip", "An outbound ride and a return ride", ArrowLeftRight]];
const SCHED_KINDS = [["one_way", "One Way", "One pickup, one destination, a future time", ArrowRight],
  ["split", "Split Trip", "Two required legs under one booking", Split], ["multi_stop", "Multi-Stop", "One continuous ride with stops", Route]];

function KindCards({ kinds, value, onChange }) {
  return (
    <div className={`grid gap-2 ${kinds.length === 3 ? "grid-cols-3" : "grid-cols-2"}`} data-testid="ride-kind-picker">
      {kinds.map(([k, label, sub, Icon]) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`ride-kind-${k}`}
          className={`rounded-2xl border p-3 text-left transition-[border-color,background-color] ${value === k ? "border-primary bg-primary/10" : "border-white/10 bg-card hover:border-white/30"}`}>
          <Icon className={`h-5 w-5 ${value === k ? "text-primary" : "text-muted-foreground"}`} />
          <p className="mt-2 text-sm font-bold">{label}</p>
          <p className="text-[11px] leading-snug text-muted-foreground">{sub}</p>
        </button>
      ))}
    </div>
  );
}

function Places({ pickup, setPickup, dropoff, setDropoff }) {
  return (
    <div className="space-y-2">
      <LocationPicker kind="pickup" label="Pickup" value={pickup} onChange={setPickup} allowGps />
      <div className="flex justify-end">
        <button type="button" data-testid="swap-locations-button" onClick={() => { setPickup(dropoff); setDropoff(pickup); }}
          className="-my-1 flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
          <ArrowUpDown className="h-3.5 w-3.5" /> Swap
        </button>
      </div>
      <LocationPicker kind="dropoff" label="Destination" value={dropoff} onChange={setDropoff} allowGps />
    </div>
  );
}

function NowSlot({ opts, setOpts, quote, lead }) {
  useEffect(() => {
    if (!opts.scheduledAt) setOpts((o) => ({ ...o, scheduledAt: earliestLocal(lead) }));
  }, [opts.scheduledAt, lead, setOpts]);
  const next = quote?.schedule?.problems?.[0]?.next_available;
  useEffect(() => {
    if (next) setOpts((o) => ({ ...o, scheduledAt: toLocalInput(next) }));
  }, [next, setOpts]);
  return (
    <p className="rounded-xl bg-muted px-3 py-2 text-sm" data-testid="book-now-slot">
      Estimated pickup: <span className="font-semibold">{opts.scheduledAt ? when(new Date(opts.scheduledAt).toISOString()) : "finding a slot…"}</span>
    </p>
  );
}

function RepeatToggle({ flow, setFlow, opts, setOpts, config, firstAt }) {
  const toggle = (v) => {
    setFlow({ ...flow, repeat: v });
    if (v && !opts.days.length && firstAt) setOpts((o) => ({ ...o, days: [(new Date(firstAt).getDay() + 6) % 7] }));
  };
  return (
    <div className="space-y-3 rounded-2xl border border-white/10 bg-card p-4" data-testid="repeat-section">
      <label className="flex items-center justify-between gap-3">
        <span><span className="flex items-center gap-2 text-sm font-bold tracking-wide"><Repeat className="h-4 w-4 text-primary" />REPEAT THIS RIDE?</span>
          <span className="text-xs text-muted-foreground">Each date gets its own ride record{flow.kind === "split" ? " with both legs" : ""}.</span></span>
        <Switch checked={flow.repeat} onCheckedChange={toggle} data-testid="repeat-toggle" />
      </label>
      {flow.repeat && !opts.days.length && <p className="text-xs text-amber-200" data-testid="repeat-pick-day">Pick at least one day to see your price.</p>}
      {flow.repeat && <TypeOptions opts={{ ...opts, bookingType: "recurring" }} setOpts={(o) => setOpts({ ...o, bookingType: opts.bookingType })} config={config} hideKind />}
    </div>
  );
}

function Status({ quote }) {
  if (quote.loading) return <p className="flex items-center gap-2 py-2 text-sm text-muted-foreground" data-testid="quote-loading"><Loader2 className="h-4 w-4 animate-spin" /> Calculating road mileage and fare…</p>;
  if (quote.error) return <p className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="quote-error">{quote.error}</p>;
  return null;
}

function useSubmit({ flow, trip, onBooked }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const submit = async (notes, forReview = false) => {
    setBusy(true);
    try {
      const path = flow.kind === "split" ? "/rides/split" : "/rides";
      const { data } = await api.post(path, { ...trip, notes: notes.trim() || null, ...(flow.kind === "split" ? {} : { submit_for_review: forReview }) });
      const n = data.rides.length;
      toast.success(forReview ? "Sent for pricing review" : n > 1 ? `${n} rides requested` : "Request sent. Scoopz will confirm shortly.");
      ["activeRide", "myRides", "credit", "mySeries", "myProfile"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      onBooked();
    } catch (e) {
      toast.error(formatApiError(e), { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };
  return { busy, submit };
}

export function BookFlow({ flow, setFlow, places, stops, setStops, legs, setLegs, opts, setOpts, trip, quote, onBooked }) {
  const [notes, setNotes] = useState("");
  const { data: config } = useQuery({ queryKey: ["pricingConfig"], queryFn: () => api.get("/pricing/config").then((r) => r.data) });
  const { busy, submit } = useSubmit({ flow, trip, onBooked: () => { setNotes(""); onBooked(); } });
  const q = trip ? quote.data : null;
  const lead = config?.scheduling?.lead_minutes ?? 10;
  const split = flow.kind === "split";
  const blocked = q?.schedule?.ok === false;
  const ready = q && q.status === "ok" && q.checkout?.ok && !q.checkout.errors?.length && !blocked && !(opts.discountCode && !q.checkout.discount);
  const setMode = (mode) => { setFlow({ mode, kind: null, repeat: false, step: "edit" }); setOpts((o) => ({ ...o, scheduledAt: "", payMethod: "pay_at_pickup" })); };

  if (flow.step === "review" && ready) {
    return (
      <div className="space-y-3">
        <SectionTitle>Review your booking</SectionTitle>
        <Review flow={flow} quote={q} opts={opts} pickup={places.pickup} dropoff={places.dropoff} stops={stops} legs={legs} />
        <Textarea data-testid="ride-notes-input" placeholder="Special instructions (optional): gate code, which entrance…" value={notes} maxLength={280} onChange={(e) => setNotes(e.target.value)} className="rounded-2xl" />
        <Button data-testid="request-ride-button" onClick={() => submit(notes)} disabled={busy} className="h-12 w-full rounded-full text-base font-semibold">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : `Confirm booking · ${money(q.checkout.amount_due)} due`}
        </Button>
        <Button variant="ghost" className="w-full rounded-full" onClick={() => setFlow({ ...flow, step: "edit" })} data-testid="review-back-button">Back to edit</Button>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="book-flow">
      <Segmented testid="booking-mode" value={flow.mode} onChange={setMode} options={[{ value: "now", label: "BOOK NOW" }, { value: "scheduled", label: "SCHEDULED RIDES" }]} />
      {flow.mode === "now" && (
        <p className="flex gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 text-xs" data-testid="book-now-notice">
          <Zap className="h-4 w-4 shrink-0 text-primary" />
          <span>Your ride will be scheduled in the next available pickup slot, approximately 5–15 minutes. Actual timing depends on Scoopz scheduling and driver availability.</span>
        </p>
      )}
      <KindCards kinds={flow.mode === "now" ? NOW_KINDS : SCHED_KINDS} value={flow.kind} onChange={(kind) => setFlow({ ...flow, kind, repeat: flow.mode === "now" ? false : flow.repeat })} />
      {flow.kind && (
        <>
          {split ? <SplitLegs legs={legs} setLegs={setLegs} lead={lead} /> : <Places {...places} />}
          {flow.kind === "multi_stop" && <StopsEditor stops={stops} setStops={setStops} tiers={config?.wait_tiers} />}
          <TripOptions opts={opts} setOpts={setOpts} config={config} hideWhen={flow.mode === "now" || split} />
          {flow.mode === "now" && !split && <NowSlot opts={opts} setOpts={setOpts} quote={q} lead={lead} />}
          {flow.kind === "round_trip" && <TypeOptions opts={{ ...opts, bookingType: "round_trip" }} setOpts={(o) => setOpts({ ...o, bookingType: opts.bookingType })} config={config} />}
          {flow.mode === "scheduled" && <RepeatToggle flow={flow} setFlow={setFlow} opts={opts} setOpts={setOpts} config={config} firstAt={split ? legs[0].at : opts.scheduledAt} />}
          <Status quote={quote} />
          {q && q.status === "ok" && !split && <QuoteCard quote={q} pickup={places.pickup} dropoff={places.dropoff} />}
          {q && q.status !== "ok" && !split && <QuoteProblem quote={q} onRetry={quote.retry} />}
          {q && q.status !== "ok" && split && <p className="rounded-xl bg-amber-400/10 p-3 text-sm text-amber-200" data-testid="split-pricing-review">One of the legs needs pricing review. Please contact Scoopz Admin to book this trip.</p>}
          {q && q.status === "ok" && <CheckoutPanel quote={q} opts={opts} setOpts={setOpts} allowPost={flow.mode === "scheduled"} summary={false} />}
          {q && !split && flow.mode === "scheduled" && <ScheduleNotice schedule={q.schedule} setOpts={setOpts} />}
          {q && split && <SplitNotice schedule={q.schedule} setLegs={setLegs} />}
          {q?.checkout?.errors?.length > 0 && <p className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="checkout-error">{q.checkout.errors[0]}</p>}
          {q && q.status === "ok" && (
            <Button data-testid="review-booking-button" disabled={!ready || quote.loading} onClick={() => setFlow({ ...flow, step: "review" })} className="h-12 w-full rounded-full text-base font-semibold">
              Review booking · {money(q.checkout?.total)}
            </Button>
          )}
          {q && q.status !== "ok" && !split && !flow.repeat && (
            <Button data-testid="submit-pricing-review-button" variant="outline" disabled={busy || blocked} onClick={() => submit(notes, true)} className="h-12 w-full rounded-full text-base font-semibold">
              Submit for admin quote
            </Button>
          )}
        </>
      )}
    </div>
  );
}
